import type { SessionBlockFormikType } from "@/modules/sessions/sessionForm/sessionBlockFormikTypes"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { SessionBlockEntityFactory } from "@/modules/sessions/entities/sessionBlockEntity"
import type {
  BoxSessionCreateInputDto,
  BoxSessionUpdateInputDto,
} from "@/modules/boxSlotSession/gateway/boxSlotSessionDto"
import { BoxSlotTypeOf } from "@/modules/boxSlot/boxSlotTypes"
import { word } from "@/modules/core/utils/i18n"

export type BoxSessionFormikState = Omit<BoxSessionFormikEntity, "toCreateInputDto" | "toUpdateInputDto">

export class BoxSessionFormikEntity {
  name: string
  description: string
  date?: Date
  blocks: SessionBlockFormikType[]
  gymnasticTag: boolean
  weightliftingTag: boolean
  cardioTag: boolean
  hideSessionContent: boolean
  _type: typeof BoxSlotTypeOf

  private constructor(state: BoxSessionFormikState) {
    this.name = state.name
    this.description = state.description
    this.date = state.date
    this.blocks = state.blocks
    this.gymnasticTag = state.gymnasticTag
    this.weightliftingTag = state.weightliftingTag
    this.cardioTag = state.cardioTag
    this.hideSessionContent = state.hideSessionContent
    this._type = state._type
  }

  static fromEntityToFormik(entity: BoxSessionDetailsEntity, date: Date): BoxSessionFormikEntity {
    return new BoxSessionFormikEntity({
      _type: BoxSlotTypeOf,
      name: entity.name,
      description: entity.description,
      date: entity.date ?? date,
      blocks: entity.blocks.map((block) => SessionBlockEntityFactory.fromEntityToFormik(block)),
      gymnasticTag: entity.gymnasticTag,
      weightliftingTag: entity.weightliftingTag,
      cardioTag: entity.cardioTag,
      hideSessionContent: entity.hideSessionContent,
    })
  }

  static getDraftFormik(date: Date): BoxSessionFormikEntity {
    return new BoxSessionFormikEntity({
      _type: BoxSlotTypeOf,
      name: word("program.session.name.default"),
      description: "",
      blocks: [],
      date: date,
      gymnasticTag: false,
      weightliftingTag: false,
      cardioTag: false,
      hideSessionContent: false,
    })
  }

  toCreateInputDto(programId: string): BoxSessionCreateInputDto {
    return {
      programId,
      name: this.name,
      description: this.description,
      blocks: this.blocks.map((block, index) => SessionBlockEntityFactory.fromFormikToInputDto(block, index)),
      hideSessionContent: this.hideSessionContent,
      weightliftingTag: this.weightliftingTag,
      gymnasticTag: this.gymnasticTag,
      cardioTag: this.cardioTag,
    }
  }

  toUpdateInputDto(): BoxSessionUpdateInputDto {
    return {
      name: this.name,
      description: this.description,
      blocks: this.blocks.map((block, index) => SessionBlockEntityFactory.fromFormikToInputDto(block, index)),
      hideSessionContent: this.hideSessionContent,
      weightliftingTag: this.weightliftingTag,
      gymnasticTag: this.gymnasticTag,
      cardioTag: this.cardioTag,
    }
  }
}
